<template>
  <div
    class="mall-product-card"
    :class="['mall-product-card--' + size, { 'mall-product-card--selected': selected }]"
    @click="$emit('click')"
  >
    <div class="mall-product-card__thumb">
      <!-- 이미지가 없으면 빈 상자만 노출 -->
      <img v-if="product.image" :src="product.image" :alt="product.name" />
    </div>
    <div class="mall-product-card__info">
      <div class="mall-product-card__title">
        <span v-if="product.purchaseDate" class="mall-product-card__date">구매일 {{ product.purchaseDate }}</span>
        <span class="mall-product-card__name">{{ product.name }}</span>
      </div>
      <div class="mall-product-card__price-area">
        <div class="mall-product-card__price-row">
          <span v-if="product.discountRate" class="mall-product-card__discount">{{ product.discountRate }}%</span>
          <span class="mall-product-card__price">{{ formatPrice(product.price) }}</span>
        </div>
        <span v-if="product.originalPrice" class="mall-product-card__original">{{ formatPrice(product.originalPrice) }}</span>
      </div>
      <div v-if="showRebuy" class="mall-product-card__rebuy" @click.stop="$emit('rebuy-click')">
        <span class="mall-product-card__rebuy-text">다시 구매하기</span>
      </div>
    </div>
  </div>
</template>

<script>
import convert from '@/utilities/convert.js'

export default {
  name: 'MallProductCard',
  props: {
    // { name, image, price, discountRate, originalPrice, purchaseDate }
    product: {
      type: Object,
      required: true,
    },
    // 'sm': 홈 카테고리 가로 목록(132px) / 'md': 목록·최근 구매(160px)
    size: {
      type: String,
      default: 'md',
    },
    selected: {
      type: Boolean,
      default: false,
    },
    showRebuy: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    formatPrice(price) {
      return convert.setComma(price) + '원'
    },
  },
}
</script>

<style scoped>
.mall-product-card {
  display: flex;
  flex-direction: column;
  background: #FFF;
  cursor: pointer;
}

.mall-product-card__thumb {
  position: relative;
  border: 1px solid #E5E8EB;
  border-radius: 12px;
  background: #FFF;
  box-sizing: border-box;
  overflow: hidden;
}

.mall-product-card__thumb img {
  position: absolute;
  object-fit: cover;
}

/* 선택된 상품 */
.mall-product-card--selected .mall-product-card__thumb {
  border-color: #0096FF;
  box-shadow: inset 0 0 0 1px #0096FF;
}

.mall-product-card__info {
  display: flex;
  flex-direction: column;
}

.mall-product-card__title {
  display: flex;
  flex-direction: column;
}

/* 13px 텍스트는 Figma 렌더링 기준(150% → 20px 반올림)으로 맞춤 */
.mall-product-card__date {
  color: #8B95A1;
  font-family: Pretendard;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  line-height: 20px;
}

/* 단어 단위 줄바꿈, 최대 2줄 */
.mall-product-card__name {
  display: -webkit-box;
  overflow: hidden;
  color: #4E5968;
  font-family: Pretendard;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-normal);
  word-break: keep-all;
  overflow-wrap: anywhere;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.mall-product-card__price-area {
  display: flex;
  flex-direction: column;
}

.mall-product-card__price-row {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.mall-product-card__discount {
  color: #F04452;
  font-family: Pretendard;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-normal);
}

.mall-product-card__price {
  color: var(--color-text-main);
  font-family: Pretendard;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-normal);
}

.mall-product-card__original {
  color: #B0B8C1;
  font-family: Pretendard;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  line-height: 20px;
  text-decoration: line-through;
}

.mall-product-card__rebuy {
  display: flex;
  height: 32px;
  margin-top: auto;
  padding: 0 8px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #EFF9FF;
}

.mall-product-card__rebuy-text {
  color: #0096FF;
  font-family: Pretendard;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-normal);
  white-space: nowrap;
}

/* sm: 132px 썸네일 (홈 카테고리 가로 목록) */
.mall-product-card--sm {
  flex-shrink: 0;
  width: 132px;
  gap: 4px;
}

.mall-product-card--sm .mall-product-card__thumb {
  width: 132px;
  height: 132px;
}

.mall-product-card--sm .mall-product-card__thumb img {
  top: -2px;
  left: -2px;
  width: 134px;
  height: 134px;
}

.mall-product-card--sm .mall-product-card__info {
  padding: 0 2px;
  gap: 2px;
}

/* md: 부모 너비 기준 정사각형 썸네일 (목록, 최근 구매) */
.mall-product-card--md {
  width: 100%;
}

/* 정사각형: 너비 - 테두리(2px) 만큼 padding */
.mall-product-card--md .mall-product-card__thumb {
  width: 100%;
  padding-top: calc(100% - 2px);
}

.mall-product-card--md .mall-product-card__thumb img {
  top: -1px;
  left: -1px;
  width: calc(100% + 2px);
  height: calc(100% + 2px);
}

.mall-product-card--md .mall-product-card__info {
  flex: 1;
  padding: 8px 4px;
  gap: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/new/molecules/MallProductCard.vue